import React, { useEffect, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Flex } from "antd";
import ProgressSegment from "./components/progressSegment/ProgressSegment";
import { InterviewSession } from "../../Interface/Index";
import InterviewService from "../../Services/interview.service";
import { DashboardContext } from "../../Context/DashboardContext";
import LoaderContext from "../../Context/LoaderContext/Index";
import ExitFullScreenConfirmation from "./components/modals/ExitFullScreenConfirmation";
import RetryEvaluationModal from "./components/modals/RetryEvaluationModal";
import StartInterviewModal from "./components/modals/StartInterviewModal";
import CamRecorder from "../../Components/CamRecorder/CamRecorder";
import Draggable from "react-draggable";
import AllowCameraConfirmation from "./components/modals/AllowCameraConfirmation";
import WarningMessage from "./components/WarningMessage";
import InterviewRoundContent from "./components/interviewRoundContent/InterviewRoundContent";
import QuestionActionButtons from "./components/interviewRoundContent/QuestionActionButtons";
import { useInterviewRoundContext } from "../../Context/InterviewRoundContext";
import useInterviewRoundsHandlers from "./hooks/useInterviewRoundsHandlers";
import "./index.scss";
import ProctoringWatcher from "./components/ProctoringWatcher";
import useProctoring from "./hooks/useProctoring";
import useRoundValidator from "./hooks/useRoundValidator";
import usePollingFunctions from "./hooks/usePollingFunctions";

const InterviewRounds: React.FC = () => {
  const { sessionId } = useParams();

  const [isSameDevice, setIsSameDevice] = useState(true);
  const [terminatedStats, setTerminatedStats] = useState({
    isLimitReached: false,
    violationType: "",
  });

  const interviewService = useMemo(() => new InterviewService(), []);

  // Dashboard Context values
  const {
    interviewSessions,
    appliedJob,
    setAppliedJob,
    refetchSessionByJobApplicationId,
    updateSession,
  } = useContext(DashboardContext);

  // Common Loader Context
  const { setLoading, setLoadingMessage, isLoading, loadingMessage } =
    useContext(LoaderContext);

  // Polling functions
  const { checkForNextRoundQuestions } = usePollingFunctions();

  // Interview Round Context States And SetStateActions
  const {
    currentSession,
    setCurrentSession,
    currentSessionQuestions,
    setCurrentSessionQuestions,
    currentSessionQuestionAnswers,
    setCurrentSessionQuestionAnswers,
    isRoundFinished,
    setIsRoundFinished,
    showExitConfirmation,
    setShowExitConfirmation,
    exitTimer,
    isFullscreen,
    showRetryEvaluation,
    activeQuestionIndex,
    setActiveQuestionIndex,
    showCamRecorder,
    showCameraConfirmation,
    showStartInterviewModal,
    elementRef,
  } = useInterviewRoundContext();

  // Interview Round Handlers
  const {
    handleEvaluation,
    handleCameraAccessConfirmation,
    handleCameraAccessdenied,
    handleCancelCameraAccessConfirmation,
    handleStartInterviewConfirmation,
    handleCancelStartInterview,
    handleRetryEvaluation,
    handleCancelExit,
    handleConfirmExit,
    handleAnswerSingleQuestion,
    handleBack,
    handleNext,
    handleQuestionAnswerChange,
    handleStartInterview,
    onNavigateToQuestions,
  } = useInterviewRoundsHandlers();

  const { handleFullScreenExitAttempt, handlePageRefesh } = useProctoring();

  const { checkViolationLimitReached, isUserUsingSameDevice } =
    useRoundValidator();

  // Effect to init the current interview round
  useEffect(() => {
    if (!sessionId || interviewSessions.length === 0) return;
    if (currentSession && currentSession._id === sessionId) return;

    const fetchData = async (
      sessionId: string,
      sessions: InterviewSession[]
    ) => {
      await refetchSessionByJobApplicationId();
      try {
        setLoading(true);
        setLoadingMessage("Loading Interview Session, please wait...");

        const session = sessions.find((s) => s._id === sessionId);
        if (!session) {
          console.error(`No session found with ID ${sessionId}`);
          return;
        }
        setCurrentSession(session);

        if (!session.isQuestionsGenerated) {
          setLoadingMessage("Generating questions, please wait...");
          const updated = await checkForNextRoundQuestions(session._id);
          updateSession(session._id, updated);
          setCurrentSession(updated);
        }

        const isRoundStarted = session.isLive;
        const isSameDevice = await isUserUsingSameDevice(session);
        const isRoundCompleted =
          session.status !== "pending" || appliedJob?.status !== "applied";

        setIsSameDevice(isSameDevice);
        setTerminatedStats(checkViolationLimitReached(session));

        // case1: user already started inetrview and opened on different device
        if (!isSameDevice && isRoundStarted && !isRoundCompleted) {
          setActiveQuestionIndex(-1);
          return;
        }

        // case2: user already started interview and opened on same device then perform refresh violation
        if (isSameDevice && isRoundStarted && !isRoundCompleted) {
          const response = await handlePageRefesh(session);
          if (response) {
            setCurrentSession(response.interviewSession);
            setAppliedJob(response.jobApplication);
          }
          setActiveQuestionIndex(-1);
          return;
        }

        // case3: user already completed interview
        if (isRoundCompleted) {
          setLoadingMessage("Loading questions, please wait...");

          const { isLimitReached } = checkViolationLimitReached(session);
          const questions = await interviewService.getQuestionsBySessionId(
            session._id
          );

          if (!questions || questions.length === 0) {
            console.error("No questions found for the session");
            return;
          }

          const answers = questions.map((q) => ({
            questionId: q._id,
            answer: q.responses?.[0]?.description ?? null,
            fileUrl:
              q.type === "fileUpload"
                ? q.responses?.[0]?.fileUrl ?? null
                : null,
          }));

          setCurrentSessionQuestions(questions);
          setCurrentSessionQuestionAnswers(answers);
          setActiveQuestionIndex(isLimitReached ? -1 : questions.length);
        } else {
          setActiveQuestionIndex(-1);
        }

        setIsRoundFinished(isRoundCompleted);
      } catch (error) {
        console.error("Error loading interview session:", error);
      } finally {
        setLoading(false);
        setLoadingMessage(null);
      }
    };

    fetchData(sessionId, interviewSessions);
  }, [sessionId, interviewSessions]);

  const handleConfirmAndFullscreenExit = async () => {
    handleConfirmExit();
    if (currentSession) {
      return await handleFullScreenExitAttempt(currentSession);
    }
  };

  return (
    <section className="interview_round_main_container">
      <ProctoringWatcher />
      <div
        className="interview_round_fullscreen_container"
        ref={elementRef}
        tabIndex={0}
      >
        {/* RETRY EVALUATION MODAL */}
        <RetryEvaluationModal
          containerRef={elementRef}
          showEvaluation={showRetryEvaluation}
          handleRetryEvaluation={handleRetryEvaluation}
        />

        {/* EXIT FULLSCREEN CONFIRMATION MODAL */}
        <ExitFullScreenConfirmation
          handleConfirmExit={handleConfirmAndFullscreenExit}
          containerRef={elementRef}
          isFullscreen={isFullscreen}
          showExitConfirmation={showExitConfirmation}
          setShowExitConfirmation={setShowExitConfirmation}
          handleCancelExit={handleCancelExit}
          exitTimer={exitTimer}
        />

        {/* START INTERVIEW MODAL */}
        <StartInterviewModal
          isOpen={showStartInterviewModal}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          onConfirm={handleStartInterviewConfirmation}
          onCancel={handleCancelStartInterview}
          containerRef={elementRef}
        />

        {/* Displaying alert message only if the round is not started or finished */}
        {activeQuestionIndex >= 0 && (
          <WarningMessage isRoundFinished={isRoundFinished} />
        )}

        {/* PROGRESS BAR */}
        {activeQuestionIndex >= 0 && (
          <Flex align="center" justify="center" style={{ width: "100%" }}>
            <ProgressSegment
              totalQuestions={currentSessionQuestions.length}
              currentQuestion={activeQuestionIndex}
              onQuestionClick={handleAnswerSingleQuestion}
              isRoundStarted={activeQuestionIndex > -1}
              isRoundFinished={isRoundFinished}
            />
          </Flex>
        )}

        {/* INTERVIEW ROUND DYNAMIC CONTENT */}
        {currentSession && appliedJob && (
          <InterviewRoundContent
            sessionQuestions={currentSessionQuestions}
            activeQuestionIndex={activeQuestionIndex}
            currentSession={currentSession}
            currentSessionQuestions={currentSessionQuestions}
            handleStartInterview={handleStartInterview}
            handleBack={handleBack}
            handleNext={handleNext}
            isRoundFinished={isRoundFinished}
            appliedJob={appliedJob}
            handleEvaluation={handleEvaluation}
            currentSessionQuestionAnswers={currentSessionQuestionAnswers}
            onNavigateToQuestions={onNavigateToQuestions}
            handleQuestionAnswerChange={handleQuestionAnswerChange}
            containerRef={elementRef}
            isSameDevice={isSameDevice}
            terminatedStats={terminatedStats}
          />
        )}

        {/* RENDERING QUESTIONS ACTION BUTTONS */}
        {activeQuestionIndex >= 0 &&
          activeQuestionIndex < currentSessionQuestions.length && (
            <QuestionActionButtons
              activeQuestionIndex={activeQuestionIndex}
              currentSessionQuestionAnswers={currentSessionQuestionAnswers}
              currentSessionQuestions={currentSessionQuestions}
              handleBack={handleBack}
              handleNext={handleNext}
              isRoundFinished={isRoundFinished}
            />
          )}

        {showCamRecorder && (
          <Draggable>
            <div
              className="video-recorder-container"
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 9999,
                cursor: "grab",
              }}
            >
              <CamRecorder
                handleCameraAccessdenied={handleCameraAccessdenied}
              />
            </div>
          </Draggable>
        )}
      </div>

      {/* CAMERA CONFIRMATION MODAL */}
      <AllowCameraConfirmation
        containerRef={elementRef}
        isOpen={showCameraConfirmation}
        handleCancelExit={handleCancelCameraAccessConfirmation}
        handleOk={handleCameraAccessConfirmation}
      />
    </section>
  );
};

export default InterviewRounds;
